import _ from "lodash";
import moment from "moment";
import { observable, makeObservable, action, computed, flow } from "mobx";
import { ESessionType, ISessionGroup, ISession } from "./type";
import {
  SESSION_PAGE_SIZE,
  SESSION_TYPE_ORDER,
  getTypeByTime,
} from "./constants";
import { CHAT_ON_R1, CLIENT_CHAN } from "logic/src/objects/chat/constants";
import * as richmodalService from "../../services/richmodal";
import { genFuzzyMatchingReg } from "./utils";
import Session from "./model";

class SessionManager {
  @observable query: string = "";
  @observable isQuerying: boolean = false;
  @observable queryList: ISession[];
  @observable isFetchList: boolean = false;
  @observable.ref list: ISessionGroup[];

  client_chan: string = CHAT_ON_R1 + ";" + CLIENT_CHAN;

  public constructor() {
    makeObservable(this);
    this.query = "";
    this.queryList = [];
    this.isQuerying = false;
    this.list = [];
    this.isFetchList = false;
    this.listMember = this.listMember.bind(this);
    this.delete = this.delete.bind(this);
    this.pinned = this.pinned.bind(this);
    this.unPinned = this.unPinned.bind(this);
  }

  @computed
  get lastSessionId() {
    if (!this.list.length) return "";
    const lastGroup = _.last(this.list);
    if (!lastGroup?.session_list.length) return "";
    return _.last(lastGroup.session_list)?.session_id || "";
  }

  @computed
  get sessionListLen() {
    return this.list.reduce((prev, cur) => {
      prev += cur.session_list.length;
      return prev;
    }, 0);
  }

  @computed
  get lastQuerySessionId() {
    if (!this.queryList.length) return "";
    return _.last(this.queryList)?.session_id || "";
  }

  @computed
  get queryListGroup(): ISessionGroup[] {
    return [
      {
        type: ESessionType.today, // 无意义，仅为符合ISessionGroup格式
        session_list: this.queryList,
      },
    ];
  }

  @computed
  get pureSessionList() {
    const tempList = this.list.reduce((prev, cur) => {
      prev.push(...(cur.session_list || []));
      return prev;
    }, [] as ISession[]);
    return this.sortListByTime(tempList);
  }

  setClientChan(v: string) {
    this.client_chan = v;
  }

  @action.bound
  updateSessionTitle(id: string, title: string) {
    this.updateListSession(id, { title });
    this.updateQuerySession(id, { title });
  }

  @flow
  public *listMember(page_size?: number, isStart?: boolean) {
    this.isFetchList = true;
    const next = isStart ? "" : this.lastSessionId;
    const { message: session_list } = yield richmodalService.getSessionList({
      page_size: page_size || SESSION_PAGE_SIZE,
      next,
      client_chan: this.client_chan,
    });
    const sortedList = this.sortListByType(session_list || []);
    if (!next) {
      this.list = sortedList;
    } else {
      this.addListSessionGroup(sortedList);
    }
    this.isFetchList = false;
  }

  @flow.bound
  *listQueryMember(page_size?: number, isStart?: boolean) {
    this.isQuerying = true;
    const next = isStart ? "" : this.lastQuerySessionId;
    const {
      message: { session_list },
    } = yield richmodalService.getQuerySessionList({
      keyword: this.query,
      page_size: page_size || SESSION_PAGE_SIZE,
      next,
      client_chan: this.client_chan,
    });
    !next
      ? (this.queryList = session_list || [])
      : this.queryList.push(...(session_list || []));
    this.isQuerying = false;
  }

  @action.bound
  querySession(input: string) {
    if (!input.length) {
      this.query = "";
      this.queryList = [];
      return;
    }
    this.query = input;
    this.listQueryMember(undefined, true);
  }

  @action.bound
  fetchMoreSession() {
    this.query ? this.listQueryMember() : this.listMember();
  }

  @flow.bound
  *editNameById(id: string, name: string) {
    yield richmodalService.editSessionTitle({
      session_id: id,
      title: name,
    });
  }

  @flow
  public *delete(id: string) {
    yield richmodalService.deleteSession({
      session_id: id,
    });
    // yield this.listMember();
    this.deleteListSession(id);
    if (this.query) {
      this.deleteQuerySession(id);
      yield this.listQueryMember(1);
    }
    yield this.listMember(1);
  }

  // 置顶
  @flow
  public *pinned(id: string) {
    yield richmodalService.pinnedSession({
      session_id: id,
    });
    // yield this.listMember();
    this._pinnedCallback(id, true);
  }

  // 取消置顶
  @flow
  public *unPinned(id: string) {
    yield richmodalService.unPinnedSession({
      session_id: id,
    });
    if (this.sessionListLen < SESSION_PAGE_SIZE - 1) {
      yield this.listMember(SESSION_PAGE_SIZE, true);
    } else {
      this._pinnedCallback(id, false);
    }

    // this._pinnedCallback(id, false);
  }

  @action
  _pinnedCallback(id: string, isTop: boolean) {
    this.updateListSession(id, { is_top: isTop });
    this.updateQuerySession(id, { is_top: isTop });
    const _lastSession = _.last(this.pureSessionList);
    let cur = this.deleteListSession(id);
    if (isTop) {
      !cur && (cur = this.getQuerySession(id) || null);
      !!cur && this.addListSession(cur);
    } else {
      if (!cur) return;
      if (cur?.session_id === _lastSession?.session_id) {
        this.listMember(1);
      } else if (cur?.time) {
        const curType = getTypeByTime(cur.time);
        const sessionGroup = this.list.find(item => item.type === curType);
        if (sessionGroup) {
          sessionGroup.session_list = this.sortListByTime([
            ...sessionGroup.session_list,
            cur,
          ]);
        } else if (curType) {
          this.listMember(1);
        }
      }
    }
  }

  @action.bound
  private pinnedInTimeGroup(id: string) {
    let session!: ISession;
    this.list.forEach((item: ISessionGroup) => {
      const sessionIndex = item.session_list.findIndex(
        (item: ISession) => item.session_id === id,
      );
      if (sessionIndex > -1) {
        session = item.session_list.splice(sessionIndex, 1)[0];
      }
    });
    if (session) {
      const todaySessionGroup = this.list.find(
        (item: ISessionGroup) => item.type === ESessionType.today,
      );
      todaySessionGroup?.session_list.unshift(session);
    }
  }

  // 如果是非置顶类型，则置顶到今天类型的第一个，如果是置顶类型，则置顶到置顶类型的第一个
  @action.bound
  public pinnedInGroup(id: string) {
    const { type, sessionIndex, session } = this.getListSessionById(id);

    if (session) {
      const needUpdate = !(
        [ESessionType.top, ESessionType.today].includes(type!) &&
        sessionIndex === 0
      );
      session.time = moment().format("YYYY-MM-DD HH:mm:ss");
      if (needUpdate) {
        const cur = this.deleteListSession(id)!;
        this.addListSession(cur);
      }
    } else {
      const curSession = this.getQuerySession(id);
      if (!curSession) return;
      curSession.time = moment().format("YYYY-MM-DD HH:mm:ss");
      this.addListSession(curSession);
    }
  }

  // @flow
  // public async unPinned(id: string) {}

  @action.bound
  addListSessionGroup(groupList: ISessionGroup[], isSort?: boolean) {
    const groupMap = groupList.reduce(
      (prev, cur) => {
        prev[cur.type] = cur;
        return prev;
      },
      {} as Record<string, ISessionGroup>,
    );

    const addedListType: string[] = [];
    this.list.forEach(item => {
      if (groupMap[item.type]) {
        item.session_list.push(...(groupMap[item.type].session_list || []));
        if (isSort) {
          item.session_list = this.sortListByTime(item.session_list);
        }
        addedListType.push(item.type);
      }
    });

    const restList = groupList.filter(
      item => !addedListType.includes(item.type),
    );
    this.list.push(...restList);
    if (isSort) {
      this.list = this.sortListByType(this.list);
    }
  }

  @action.bound
  addListSession(session: ISession, add2Query?: boolean) {
    console.log("-----------addListSession----------", session);
    const type = session.is_top
      ? ESessionType.top
      : getTypeByTime(session.time);

    const typeIndex = this.list.findIndex(item => item.type === type);
    if (typeIndex > -1) {
      this.list[typeIndex].session_list.push(session);
      this.list[typeIndex].session_list = this.sortListByTime(
        this.list[typeIndex].session_list,
      );
    } else {
      this.list.unshift({
        type: type,
        session_list: [session],
      });
      this.list = this.sortListByType(this.list);
    }

    add2Query && this.addQuerySession(session);
  }

  @action.bound
  deleteListSession(id: string): ISession | null {
    let deleteItem: ISession | null = null;

    const deletedIndex: number[] = [];
    this.list.forEach((item, groupIndex) => {
      if (item.session_list) {
        const index = item.session_list.findIndex(
          item => item.session_id === id,
        );
        if (index > -1) {
          deleteItem = item.session_list[index];
          item.session_list.splice(index, 1);
        }
        !item.session_list.length && deletedIndex.push(groupIndex);
      }
    });
    // 删除空分组
    if (deletedIndex.length) {
      _.pullAt(this.list, deletedIndex);
    }
    this.list = [...this.list];
    return deleteItem;
  }

  @action.bound
  updateListSession<K extends keyof ISession>(
    id: string,
    props: { [key in K]: ISession[K] },
  ) {
    this.list.forEach(item => {
      if (item.session_list) {
        const index = item.session_list.findIndex(
          item => item.session_id === id,
        );
        if (index > -1) {
          if (item.session_list[index] instanceof Session) {
            Object.entries(props).forEach(([key, v]) => {
              // @ts-ignore
              item.session_list[index][key] = v;
            });
          } else {
            item.session_list[index] = {
              ...item.session_list[index],
              ...props,
            };
          }
        }
      }
    });

    this.list = [...this.list];
  }

  @action.bound
  getListSessionById(id: string): {
    type: ESessionType | null;
    session: ISession | null;
    groupIndex: number | null;
    sessionIndex: number | null;
  } {
    let type = null,
      session = null,
      groupIndex = null,
      sessionIndex = null;

    this.list.forEach((item: ISessionGroup, gIndex) => {
      const sIndex = item.session_list.findIndex(
        item => item.session_id === id,
      );
      if (sIndex > -1) {
        type = item.type;
        groupIndex = gIndex;
        sessionIndex = sIndex;
        session = item.session_list[sIndex];
      }
    });

    return {
      type,
      session,
      groupIndex,
      sessionIndex,
    };
  }

  @action.bound
  addQuerySession(v: ISession) {
    if (this._isMatchQuery(v.title)) {
      this.queryList.unshift(v);
    }
  }

  @action.bound
  deleteQuerySession(id: string): ISession | null {
    let deleted = null;
    const queryIndex = this.queryList.findIndex(
      query => query.session_id === id,
    );
    if (queryIndex > -1) {
      deleted = this.queryList[queryIndex];
      this.queryList.splice(queryIndex, 1);
    }
    return deleted;
  }

  @action.bound
  updateQuerySession<K extends keyof ISession>(
    id: string,
    props: { [key in K]: ISession[K] },
  ) {
    if (!this.queryList.length) return;
    this.queryList.forEach((item: ISession, index) => {
      if (item.session_id === id) {
        this.queryList[index] = { ...item, ...props };
      }
    });
    if ("title" in props && !this._isMatchQuery(props.title as string)) {
      this.deleteQuerySession(id);
      this.listQueryMember(1);
    }
  }

  @action
  getQuerySession(id: string) {
    return this.queryList.find(ele => ele.session_id === id);
  }

  sortListByTime(list: ISession[]) {
    return _.orderBy(list, item => new Date(item.time), "desc");
  }

  sortListByType(list: ISessionGroup[]) {
    return _.orderBy(
      list,
      item => SESSION_TYPE_ORDER.indexOf(item.type),
      "asc",
    );
  }

  _isMatchQuery(title: string): boolean {
    if (!this.query) return false;
    const matchRegx = genFuzzyMatchingReg(this.query);
    return !!(matchRegx && matchRegx.test(title));
  }
}

export default SessionManager;
